import apiClient from './api';
import {
  selectDrainBatch,
  markInFlight,
  applyBatchResult,
  revertInFlightToPending,
  resetStaleInFlight as _resetStale,
  reapSilentDropFailures,
} from './mutationStore';

const LOCK_NAME = 'rin-sync-drain';
const DEBOUNCE_MS = 300;
// Reason: flaky-network retry (design §5B) — while retryable work exists and the
// browser reports online, a drain must fire at least once per backoff-cap
// interval with no user action. Exponential schedule, capped at 120s.
const RETRY_BACKOFF_MS = [5000, 15000, 45000, 120000];

let debounceTimer = null;
let fallbackInFlight = null;
let retryTimer = null;
let retryDelayMs = null;
let backoffLevel = 0;

export const resetStaleInFlight = _resetStale;

// Reason: mirrors activateResetUnderLock in sw/sync.js — uses ifAvailable so a
// tab opened mid-drain skips the reset rather than flipping in_flight rows back
// to pending under an active drain.
export async function resetUnderLock() {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(
      LOCK_NAME,
      { mode: 'exclusive', ifAvailable: true },
      async (lock) => {
        if (!lock) return;
        await _resetStale();
      }
    );
  }
  await _resetStale();
}

// Reason: navigator.serviceWorker.ready NEVER resolves when no SW controls the
// page (dev, or prod before the SW activates / after an unregister). This helper
// is fire-and-forget from _runUnderLock so that can't wedge the foreground drain;
// the internal timeout just bounds the registration attempt and lets the promise
// settle instead of leaking a perpetually-pending await.
const SW_READY_TIMEOUT_MS = 1500;

export async function requestBackgroundSync() {
  if (typeof navigator === 'undefined') return;
  if (!navigator.serviceWorker || !('SyncManager' in (typeof window !== 'undefined' ? window : {}))) return;
  try {
    const reg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('serviceWorker.ready timeout')), SW_READY_TIMEOUT_MS),
      ),
    ]);
    if (reg?.sync) await reg.sync.register('flush-mutations');
  } catch {
    // Background Sync unavailable or no SW yet (Safari/iOS, dev, denied) — no-op.
  }
}

export function triggerDrain() {
  // Reason: every external trigger (mutate/online/visibility/boot/inbox retry)
  // funnels through here — it is fresh evidence conditions changed, so reset the
  // backoff to base and cancel the scheduled retry. Timer-driven re-drains call
  // _runUnderLock directly so their own failures escalate instead of resetting.
  _clearRetryTimer();
  backoffLevel = 0;
  return new Promise((resolve) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      _runUnderLock().then(resolve, resolve);
    }, DEBOUNCE_MS);
  });
}

function _clearRetryTimer() {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  retryDelayMs = null;
}

function _onRetryTimerFired() {
  retryTimer = null;
  retryDelayMs = null;
  // Reason: when the browser reports offline the timer deliberately yields —
  // the existing online/visibilitychange listeners own reconnection (§5B). The
  // timer exists for the flaky case where onLine stays true but requests fail.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve();
  return _runUnderLock();
}

// Reason: called after every drain. If retryable work remains (selectDrainBatch
// non-empty: pending, or failed with attempts < 5) schedule ONE follow-up drain
// with escalating backoff; if the retryable set is empty, reset to base so the
// next failure starts at 5s. Module-level handle mirrors the debounceTimer
// pattern — at most one scheduled retry at a time.
//
// `outcome` describes the drain that just ran:
//  - authFailed: batch POST rejected with 401/403. The api.js interceptor has
//    already cleared tokens and redirected to /login, so retrying just POSTs
//    unauthenticated on a loop while logged out. Cancel the timer; rows stay
//    pending and the next fresh trigger (login/boot/online) resumes them.
//  - progressed: at least one mutation settled (per-item ok/duplicate). A large
//    backlog (> BATCH_LIMIT) drains one batch per pass, so a healthy user with
//    100 queued mutations must NOT be throttled to 5s→15s→45s gaps. Progress
//    means the network is fine — reset backoff so the next pass fires at base.
async function _scheduleRetryIfNeeded(outcome) {
  if (outcome?.authFailed) {
    // Reason: don't burn cycles POSTing unauthenticated after a 401/403 logout.
    backoffLevel = 0;
    _clearRetryTimer();
    return;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  // Reason: this selectDrainBatch runs OUTSIDE the Web Lock, so it can race a
  // concurrent drain's writes. Both stale outcomes self-heal: a false "empty"
  // is re-covered by the writer's own post-drain schedule/trigger, and a false
  // "non-empty" costs at most one spurious (harmless, idempotent) drain. Known
  // and accepted — no lock needed here.
  const remaining = await selectDrainBatch(1);
  if (remaining.length === 0) {
    backoffLevel = 0;
    _clearRetryTimer();
    return;
  }
  // Reason: reset to base when the drain made progress; only escalate when it
  // failed or made zero progress (transport failure / all-rejected batch).
  if (outcome?.progressed) backoffLevel = 0;
  if (retryTimer) return;
  const delay = RETRY_BACKOFF_MS[Math.min(backoffLevel, RETRY_BACKOFF_MS.length - 1)];
  backoffLevel = Math.min(backoffLevel + 1, RETRY_BACKOFF_MS.length - 1);
  retryDelayMs = delay;
  retryTimer = setTimeout(() => {
    _onRetryTimerFired().catch(() => {});
  }, delay);
}

async function _runUnderLock() {
  // Reason: kick off Background Sync registration (so offline writes still get a
  // future SW flush) but DO NOT await it — serviceWorker.ready can hang forever
  // when no SW controls the page, and awaiting it here would wedge the whole
  // foreground drain so queued mutations would never POST.
  requestBackgroundSync();
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  let outcome;
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    outcome = await navigator.locks.request(LOCK_NAME, { mode: 'exclusive' }, _drain);
  } else {
    // Fallback mutex for environments without Web Locks (Safari < 15.4)
    if (fallbackInFlight) { await fallbackInFlight; return; }
    fallbackInFlight = _drain().finally(() => { fallbackInFlight = null; });
    outcome = await fallbackInFlight;
  }
  await _scheduleRetryIfNeeded(outcome);
}

// Returns an outcome descriptor consumed by _scheduleRetryIfNeeded:
//   { progressed: bool, authFailed: bool }
async function _drain() {
  const candidates = await selectDrainBatch();
  const batch = await markInFlight(candidates);
  if (batch.length === 0) return { progressed: false, authFailed: false };

  let resp;
  try {
    resp = await apiClient.post('/api/mutations/batch', {
      mutations: batch.map(m => ({ key: m.key, type: m.type, payload: m.payload })),
    });
  } catch (err) {
    await revertInFlightToPending(batch.map(m => m.key), err?.message || err);
    // Reason: 401/403 means api.js already cleared tokens + redirected to login.
    // Signal authFailed so the retry timer stops (no unauthenticated polling).
    const status = err?.response?.status;
    return { progressed: false, authFailed: status === 401 || status === 403 };
  }

  const results = resp?.data?.results || [];
  await applyBatchResult(results);
  // Reason: server may return a partial or empty results array for a 2xx; any
  // batch key still in_flight was silently omitted — revert so they retry.
  await revertInFlightToPending(batch.map(m => m.key), 'protocol: missing from batch response');
  await reapSilentDropFailures();
  // Reason: progress = the server settled at least one mutation this pass.
  // Drives backoff reset so a large backlog drains at base cadence, not throttled.
  const progressed = results.some(r => r.status === 'ok' || r.status === 'duplicate');
  return { progressed, authFailed: false };
}

export const __test_only = {
  resetState: () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = null;
    fallbackInFlight = null;
    _clearRetryTimer();
    backoffLevel = 0;
  },
  getRetryState: () => ({
    scheduled: retryTimer !== null,
    delayMs: retryDelayMs,
    backoffLevel,
  }),
  // Simulates timer expiry deterministically (invokes the real callback).
  fireRetryNow: () => {
    if (!retryTimer) return Promise.resolve();
    clearTimeout(retryTimer);
    return _onRetryTimerFired();
  },
  flushDebounce: () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  },
  drainNow: _runUnderLock,
};
